import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FxProvider } from "@/lib/fx";
import { ConfirmDialogProvider } from "@/components/ConfirmDialog";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import CampaignCreate from "@/pages/CampaignCreate";
import Approvals from "@/pages/Approvals";
import DetailedApprovals from "@/pages/DetailedApprovals";
import EmailSettings from "@/pages/EmailSettings";
import ReportingNumbers from "@/pages/ReportingNumbers";
import Billing from "@/pages/Billing";
import Clients from "@/pages/Clients";
import Users from "@/pages/Users";
import Activity from "@/pages/Activity";
import Digest from "@/pages/Digest";
import Anomalies from "@/pages/Anomalies";
import Platforms from "@/pages/Platforms";
import Templates from "@/pages/Templates";
import Maintenance from "@/pages/Maintenance";
import ClientPortal from "@/pages/ClientPortal";

import "@/App.css";

function RoleHome() {
    const { user } = useAuth();
    if (user?.role === "client_user") return <Navigate to="/portal" replace />;
    return <Dashboard />;
}

function App() {
    return (
        <AuthProvider>
            <FxProvider defaultBase="USD">
                <ConfirmDialogProvider>
                <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />

                    {/* Client portal — separate shell */}
                    <Route
                        path="/portal/*"
                        element={
                            <ProtectedRoute allowed={["client_user", "super_admin"]}>
                                <ClientPortal />
                            </ProtectedRoute>
                        }
                    />

                    {/* Internal app under Layout */}
                    <Route
                        path="/*"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Routes>
                                        <Route path="/" element={<RoleHome />} />
                                        <Route path="/campaigns" element={<Campaigns />} />
                                        <Route
                                            path="/campaigns/new"
                                            element={
                                                <ProtectedRoute allowed={["client_servicing"]}>
                                                    <CampaignCreate />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route path="/campaigns/:id" element={<CampaignDetail />} />
                                        <Route path="/approvals" element={<Approvals />} />
                                        <Route path="/detailed-approvals" element={<DetailedApprovals />} />
                                        <Route path="/settings/email" element={<EmailSettings />} />
                                        <Route path="/reporting-numbers" element={<ReportingNumbers />} />
                                        <Route path="/billing" element={<Billing />} />
                                        <Route path="/digest" element={<Digest />} />
                                        <Route path="/anomalies" element={<Anomalies />} />
                                        <Route
                                            path="/platforms"
                                            element={
                                                <ProtectedRoute allowed={["super_admin"]}>
                                                    <Platforms />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/templates"
                                            element={
                                                <ProtectedRoute allowed={["super_admin", "client_servicing"]}>
                                                    <Templates />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/maintenance"
                                            element={
                                                <ProtectedRoute allowed={["super_admin"]}>
                                                    <Maintenance />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route path="/clients" element={<Clients />} />
                                        <Route path="/users" element={<Users />} />
                                        <Route path="/activity" element={<Activity />} />
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" />
                </ConfirmDialogProvider>
            </FxProvider>
        </AuthProvider>
    );
}

export default App;
